import React, { useState } from 'react';
import { connectorService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { ShoppingCart, ArrowLeft, CheckCircle, Loader } from 'lucide-react';

function MintsoftConnectionForm({ onSuccess, onBack }) {
  const [formData, setFormData] = useState({
    connection_name: '',
    username: '',
    password: '',
    warehouse_id: '',
    sync_schedule: 'daily',
    days_back: 30
  });
  const [loading, setLoading] = useState(false);
  const { notify } = useNotification();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    if (!formData.connection_name || !formData.username || !formData.password || !formData.warehouse_id) {
      notify.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const response = await connectorService.createMintsoftConnection({
        connection_name: formData.connection_name,
        username: formData.username,
        password: formData.password,
        warehouse_id: parseInt(formData.warehouse_id),
        sync_schedule: formData.sync_schedule,
        days_back: parseInt(formData.days_back)
      });

      notify.success('Mintsoft connection created! Syncing data...');

      // Trigger initial sync
      try {
        await connectorService.syncMintsoftConnection(response.connection_id);
        notify.info('Data extraction started. This may take 5-10 minutes.');
      } catch (syncError) {
        console.warn('Initial sync trigger failed:', syncError);
      }

      if (onSuccess) onSuccess(response);
    } catch (error) {
      console.error('Error creating Mintsoft connection:', error);
      notify.error(error.response?.data?.detail || 'Failed to create connection');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={onBack}
          className="flex items-center text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        <div className="flex items-center mb-4">
          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-4">
            <ShoppingCart className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white">
              Connect Mintsoft
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              Sync inventory, orders, and product data from your Mintsoft warehouse
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">

        {/* Connection Name */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Connection Name *
          </label>
          <input
            type="text"
            value={formData.connection_name}
            onChange={(e) => setFormData({ ...formData, connection_name: e.target.value })}
            placeholder="e.g., Main Warehouse"
            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
            required
          />
        </div>

        {/* Username */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Mintsoft Username *
          </label>
          <input
            type="text"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            placeholder="e.g., user@company.com"
            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
            required
          />
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Password *
          </label>
          <input
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder="Enter your Mintsoft password"
            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
            required
          />
        </div>

        {/* Warehouse ID */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Warehouse ID *
          </label>
          <input
            type="number"
            value={formData.warehouse_id}
            onChange={(e) => setFormData({ ...formData, warehouse_id: e.target.value })}
            placeholder="e.g., 9"
            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
            required
          />
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Find this in your Mintsoft settings or contact your administrator
          </p>
        </div>

        {/* Order History Window */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Order History Window
          </label>
          <select
            value={formData.days_back}
            onChange={(e) => setFormData({ ...formData, days_back: e.target.value })}
            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          >
            <option value={30}>30 days (1 month)</option>
            <option value={60}>60 days (2 months)</option>
            <option value={120}>120 days (4 months)</option>
            <option value={240}>240 days (8 months)</option>
          </select>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            How much order history to include. Longer windows are better for trend analysis but take longer to sync.
          </p>
        </div>

        {/* Sync Schedule */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Sync Schedule
          </label>
          <select
            value={formData.sync_schedule}
            onChange={(e) => setFormData({ ...formData, sync_schedule: e.target.value })}
            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          >
            <option value="daily">Daily (Recommended)</option>
            <option value="weekly">Weekly</option>
            <option value="manual">Manual Only</option>
          </select>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            How often to automatically sync data from Mintsoft
          </p>
        </div>

        {/* What Gets Extracted */}
        <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
            Data to be extracted:
          </h4>
          <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span><strong>Current Inventory:</strong> Stock levels with expiry dates and batches</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span><strong>Product Catalog:</strong> All products with categories and lead times</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span><strong>Order History:</strong> Last {formData.days_back} days of orders with SKU details</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span><strong>Alerts:</strong> Expiry warnings and low stock notifications</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span><strong>Blocked Orders:</strong> Orders that can't ship due to stock issues</span>
            </li>
          </ul>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
          >
            {loading ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Creating Connection...
              </>
            ) : (
              'Create Connection'
            )}
          </button>
        </div>
      </form>

      {/* Help Text */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
          Need help finding your credentials?
        </h4>
        <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1">
          <li>• Login to Mintsoft at <a href="https://om.mintsoft.co.uk" target="_blank" rel="noopener noreferrer" className="underline">om.mintsoft.co.uk</a></li>
          <li>• Your username is typically your email address</li>
          <li>• Warehouse ID can be found in your warehouse settings</li>
          <li>• Contact support if you need API access enabled</li>
        </ul>
      </div>
    </div>
  );
}

export default MintsoftConnectionForm;
